/* CliniEco W2 - usage-count nudge  v1.6.1  2026-09-14
 * ------------------------------------------------------------------
 * HARD RULE #0: this module NEVER disables, hides, locks, rate-limits or
 * downgrades any capability of the compliance log tool. It only counts
 * local generations and renders ONE line of text below the tool.
 * It never calls preventDefault()/stopPropagation() and never touches
 * tool behaviour. If it ever does, it must be rolled back.
 * ------------------------------------------------------------------
 * Zero backend. localStorage only.
 *
 * v1.6 -> v1.6.1  (2026-09-14, GEO #2516 - GA4 instrumentation ONLY)
 *   Fires hook_tier_reached { tier: <index> } at the moment a tier is crossed
 *   (count moves from below the threshold to at-or-above it), inside bump().
 *   Count-only: never the content of the log. HARD RULE #0, tiers, labels, keys,
 *   counting semantics, guards, DOM and the MOUNT POSITION are all unchanged.
 * ------------------------------------------------------------------
 * v1.5 -> v1.6  (COO裁定 on GEO #2412 / W2 fix-compendium section 4.1, 2026-09-13
 *   20:5x EDT - SELF-REPORT ENTRY REMOVED. Copy/position/tiers unchanged.)
 *   v1.4/v1.5 shipped a "Used it before? Set your count" input (spec v1.3
 *   section 2). It wrote any value straight into the counter, so typing 10
 *   unlocked the 10-day physical tier with ZERO logged activity - a reward you
 *   can award yourself is not earned, and the page would be telling an untrue
 *   achievement story. There is no honest alternative: with no backend and no
 *   accounts nothing can verify a claimed past count, so the truthful boundary
 *   is the one the notice already states.
 *   Rejected (GEO option B): a display-only input makes the line contradict
 *   itself ("Days logged: 10" but tier 1 only) - swaps "gamed" for "confusing".
 *   Rejected (GEO option A): unimplementable - no server-side truth to cap to.
 *   Replacement = a request link, so a real user who changed device or cleared
 *   their browser keeps an honest path (human review). Tiers (3 / 10), labels,
 *   day semantics, guards, HARD RULE #0 and the MOUNT POSITION: unchanged.
 *   Position note: moving this line above the tool was measured (4,041px down,
 *   ~5.8 phone screens, off-screen on load) but is NOT done here - hook/coupon
 *   placement follows 胡总's presentation rule (below the output area only,
 *   never a first-screen slot / banner / popup). Parked as a proposal for him.
 * v1.4 -> v1.5  (COO裁定, 2026-09-13 20:0x EDT - COUNT SEMANTICS + WORDING)
 *   Finding (graphic-designer-od #2391, independently reproduced by writer-b
 *   #2395 and GEO's role tests): the visible text promised "Sessions logged"
 *   while the counter actually advanced on *distinct exported content*, so
 *   (a) editing ONE character re-counted and (b) re-exporting an unchanged log
 *   did NOT advance - users read that as "broken".
 *   Fix = count the thing the text says: ONE per device per CALENDAR DAY,
 *   device-local date (not UTC), only when the log has real content.
 *   Consequences: threshold 3 = three different days, 10 = ten different days
 *   (that is the retention mechanic the spec asks for - "a reason to come back").
 *   A same-day re-export is now correctly a no-op instead of a silent anomaly.
 *   Keys changed on purpose (count/hash -> days/lastday) so pre-existing
 *   inflated test counts do not leak into the new semantics.
 *   Tier thresholds/labels, guards, DOM behaviour, HARD RULE #0: unchanged.
 * ------------------------------------------------------------------
 * v1.3 -> v1.4  (GEO #2366 section 2 - WORDING ONLY, no behaviour change)
 *   progress line: the paywall-flavoured verb (gate verb / past tense) was
 *   replaced by an achievement verb - "earn" / "Earned".
 *   Rationale: this line is achievement progress, never a gate; the old verb
 *   reads like a paywall to a reader or a scanner. Tier logic, keys, counting,
 *   guards and DOM behaviour are identical to v1.3.
 * ------------------------------------------------------------------
 * v1.2 -> v1.3  (GEO #2341 "尽可能完善" items 4/5/6/8)
 *   4. persistent line: the progress line is visible as soon as the tool is
 *      mounted (previously it only appeared after the first counted export).
 *      At 0 the line carries no reward text at all.
 *   5. mount once: any previously injected line is removed before rebuilding,
 *      so SPA / partial paint can never produce two progress lines.
 *   6. mobile: wrapping text (overflow-wrap), 14px floor, tap targets for the
 *      links - verified at 375x812.
 *   8. neutral notice: "Counted per device & browser." - states the known
 *      localStorage limitation instead of letting it become a support ticket.
 * ------------------------------------------------------------------
 * v1.1 -> v1.2  (found by live acceptance run, 2026-09-13)
 *   The tool's own buttons call its INTERNAL csv()/printDoc(); they do NOT go
 *   through window.cliniecoComplianceLog. Wrapping the exported API alone
 *   therefore counted nothing on real clicks. v1.2 counts the two real user
 *   paths:
 *     (a) a capture-phase click listener on the tool host, which - only for the
 *         duration of that click - observes URL.createObjectURL (CSV produced
 *         a file) and window.open (print window really opened), then restores
 *         both globals. Programmatic API calls stay wrapped as well.
 *     (b) a real log is required: an empty form / untouched placeholder row is
 *         never counted (auto-filled dates do not make a record).
 *   Failures (blocked pop-up, thrown export) are NOT counted and are never
 *   surfaced to the user.
 */
(function () {
  'use strict';

  var MOUNT_ID  = 'clinieco-log-tool';
  var COUNT_KEY = 'clinieco_log_days_v1';
  var DAY_KEY   = 'clinieco_log_lastday_v1';
  var ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
  var KINDS     = ['loads', 'service', 'malfunctions', 'labels'];
  var TIERS = [
    { at: 3,  kind: 'code',    code: 'CLI-REWARD-20OFF', label: '$20 off a $99 order' },
    { at: 10, kind: 'request', label: 'a CliniEco biological indicator 5-pack trial (CA $12.99, shipping included)' }
  ];
  var REQUEST_URL = '/pages/wholesale?reward=BI#bulk-order-request';
  var REISSUE_URL = '/pages/contact';   /* lost-code / changed-device path = human review */
  var NOTICE = 'Counted per device and browser, one per day.';

  function readInt(k) {
    try { var n = parseInt(window.localStorage.getItem(k), 10); return isFinite(n) && n > 0 ? n : 0; }
    catch (e) { return 0; }
  }
  function writeInt(k, n) { try { window.localStorage.setItem(k, String(n)); } catch (e) {} }
  function readStr(k) { try { return String(window.localStorage.getItem(k) || ''); } catch (e) { return ''; } }
  function writeStr(k, v) { try { window.localStorage.setItem(k, String(v)); } catch (e) {} }

  /* device-LOCAL calendar day. Must not use toISOString(): that is UTC, and in
   * Toronto everything exported after 20:00 local would land on "tomorrow"
   * (same root cause as the tool's own date bug, reported as P1-1). */
  function localDay() {
    var d = new Date(), m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  /* does the state hold at least one row with user data? Mirrors the tool's own
   * hasData(): auto-filled dates and untouched placeholder rows do not count. */
  function stateHasContent() {
    var st = null;
    try { st = window.cliniecoComplianceLog && window.cliniecoComplianceLog.state; } catch (e) { st = null; }
    if (!st) { return false; }
    for (var i = 0; i < KINDS.length; i++) {
      var rows = st[KINDS[i]] || [];
      for (var j = 0; j < rows.length; j++) {
        var r = rows[j] || {};
        for (var k in r) {
          if (!Object.prototype.hasOwnProperty.call(r, k)) { continue; }
          if (k === 'id' || k === 'touched') { continue; }
          var v = r[k];
          if (v == null) { continue; }
          v = String(v);
          if (v.length === 0) { continue; }
          if (ISO_DAY_RE.test(v)) { continue; }   /* auto-filled date != content */
          return true;
        }
      }
    }
    return false;
  }

  function nextTier(n) { for (var i = 0; i < TIERS.length; i++) { if (n < TIERS[i].at) { return TIERS[i]; } } return null; }
  function reached(n)  { var out = []; for (var i = 0; i < TIERS.length; i++) { if (n >= TIERS[i].at) { out.push(TIERS[i]); } } return out; }

  /* ---------- GA4 instrumentation (GEO #2516) ----------
   * Event name + fixed label + integer tier index ONLY - never record content.
   * Swallowed on error: instrumentation must never break counting or rendering. */
  function track(name, params) {
    try {
      if (typeof window.gtag !== 'function') { return; }
      var p = { tool: 'compliance_log' };
      if (params) {
        for (var k in params) {
          if (Object.prototype.hasOwnProperty.call(params, k)) { p[k] = params[k]; }
        }
      }
      window.gtag('event', name, p);
    } catch (e) { /* ignore */ }
  }

  var el = null, numEl = null, msgEl = null, setWrap = null, linkEl = null;

  function render() {
    /* the theme re-renders the article body, which can replace our node with a
     * clone; always resolve the LIVE node instead of trusting a stale reference
     * (found by the acceptance run: localStorage advanced while the visible text
     * stayed at the old value). Keeps exactly one line. */
    var nodes = document.querySelectorAll('.clog-nudge');
    if (!nodes.length) { return; }
    for (var d = nodes.length - 1; d > 0; d--) {
      if (nodes[d].parentNode) { nodes[d].parentNode.removeChild(nodes[d]); }
    }
    el = nodes[0];
    numEl = el.querySelector('.clog-nudge-num');
    msgEl = el.querySelector('.clog-nudge-msg');
    linkEl = el.querySelector('.clog-nudge-link');
    if (!numEl || !msgEl || !linkEl) { return; }
    var n = readInt(COUNT_KEY);
    /* item 4: the line is persistent, so the user can always see where they are.
     * At 0 there is deliberately no reward / earn text (nothing is promised). */
    el.style.display = 'block';
    numEl.textContent = 'Days logged on this device: ' + n;
    var nt = nextTier(n), done = reached(n), bits = [];
    for (var i = 0; i < done.length; i++) {
      var t = done[i];
      bits.push(t.kind === 'code'
        ? 'Your ' + t.label + ' code: ' + t.code
        : 'Earned: ' + t.label + ' - request it here');
    }
    if (nt) { bits.push((nt.at - n) + ' more to earn ' + nt.label); }
    msgEl.textContent = bits.join('  |  ');
    /* request links are only added for tiers that must be human-reviewed */
    var needReq = false;
    for (var j = 0; j < done.length; j++) { if (done[j].kind === 'request') { needReq = true; } }
    linkEl.style.display = needReq ? 'inline-block' : 'none';
  }

  function build(host) {
    /* item 5: mount exactly once - drop anything a previous injection left */
    var stale = document.querySelectorAll('.clog-nudge');
    for (var i = 0; i < stale.length; i++) {
      if (stale[i].parentNode) { stale[i].parentNode.removeChild(stale[i]); }
    }
    el = document.createElement('div');
    el.className = 'clog-nudge';
    /* explicit inline styles so no theme rule can hide it (visibility is part of acceptance) */
    el.setAttribute('style', 'display:none;box-sizing:border-box;margin:12px 0 0;padding:10px 12px;' +
      'border-left:3px solid #2f6f4f;background:#f4f8f5;color:#1f2b25;font-size:14px;line-height:1.5;' +
      'max-width:100%;overflow-wrap:anywhere;');

    numEl = document.createElement('div');
    numEl.className = 'clog-nudge-num';
    numEl.setAttribute('style', 'font-weight:600;color:#1f2b25;font-size:14px;line-height:1.45;');
    msgEl = document.createElement('div');
    msgEl.className = 'clog-nudge-msg';
    msgEl.setAttribute('style', 'color:#33463c;margin-top:2px;font-size:14px;line-height:1.5;overflow-wrap:anywhere;');

    linkEl = document.createElement('a');
    linkEl.className = 'clog-nudge-link';
    linkEl.href = REQUEST_URL;
    linkEl.textContent = 'Request your reward';
    linkEl.setAttribute('style', 'display:none;margin-left:8px;color:#2f6f4f;text-decoration:underline;' +
      'font-size:14px;padding:6px 2px;');

    /* v1.6: the "Set your count" self-report entry is REMOVED (it let anyone
     * type any number and unlock a tier with zero logged activity). Replacement
     * = a request link: a real user who changed device or cleared their browser
     * still has an honest path, through a human. */
    setWrap = document.createElement('div');
    setWrap.setAttribute('style', 'margin-top:6px;font-size:13px;color:#5c6b63;line-height:1.45;');
    var reissue = document.createElement('a');
    reissue.href = REISSUE_URL;
    reissue.textContent = 'Lost your code, or changed device? Ask us to reissue';
    reissue.setAttribute('style', 'display:inline-block;color:#3c4a43;text-decoration:underline;padding:6px 2px;font-size:14px;');
    var note = document.createElement('span');
    note.textContent = ' ' + NOTICE;
    note.setAttribute('style', 'font-size:13px;color:#5c6b63;');

    setWrap.appendChild(reissue); setWrap.appendChild(note);
    el.appendChild(numEl); el.appendChild(msgEl); el.appendChild(linkEl); el.appendChild(setWrap);
    /* MOUNT POSITION UNCHANGED (immediately after the tool block, spec v1.3
     * section 2). Hook placement follows 胡总's presentation rule - never a
     * first-screen slot / banner / popup. A move to the top is parked with him. */
    host.insertAdjacentElement('afterend', el);
  }

  var pending = false;
  function bump() {
    if (pending) { return; }
    pending = true;
    setTimeout(function () {
      pending = false;
      /* an empty form is not a log - never count it */
      if (!stateHasContent()) { return; }
      /* one count per device per local calendar day: the line says "days", so
       * it must move once a day, not once per edited character. */
      var today = localDay();
      if (readStr(DAY_KEY) === today) { return; }
      writeStr(DAY_KEY, today);
      var n0 = readInt(COUNT_KEY), n1 = n0 + 1;
      writeInt(COUNT_KEY, n1);
      /* a tier is announced exactly once, at the crossing (the count only goes up) */
      for (var ti = 0; ti < TIERS.length; ti++) {
        if (n0 < TIERS[ti].at && n1 >= TIERS[ti].at) { track('hook_tier_reached', { tier: ti }); }
      }
      render();
    }, 0);
  }

  /* ---------- real user path: the tool's own buttons ----------
   * Observe (never intercept) the two export paths for the duration of one
   * click: CSV is only "successful" if it really built a file, print only if a
   * window really opened. Globals are restored immediately afterwards. */
  var watching = false;
  function watchExport(act) {
    if (watching) { return; }
    watching = true;
    var flags = { blob: false, win: undefined };
    var oBlob = (window.URL && typeof window.URL.createObjectURL === 'function') ? window.URL.createObjectURL : null;
    var oOpen = window.open;
    if (oBlob) {
      window.URL.createObjectURL = function () { flags.blob = true; return oBlob.apply(window.URL, arguments); };
    }
    window.open = function () {
      var w = oOpen.apply(window, arguments);
      flags.win = w;
      return w;
    };
    setTimeout(function () {
      if (oBlob) { window.URL.createObjectURL = oBlob; }
      window.open = oOpen;
      watching = false;
      var ok = (act === 'csv') ? flags.blob === true : (flags.win != null);
      if (ok) { bump(); }
    }, 0);
  }

  function onClickCapture(ev) {
    var t = ev.target;
    if (!t || typeof t.closest !== 'function') { return; }
    var btn = t.closest('[data-act]');
    if (!btn) { return; }
    var act = btn.getAttribute('data-act');
    if (act === 'csv' || act === 'print') { watchExport(act); }
  }

  /* ---------- programmatic path: the exported API ---------- */
  function callGuarded(fn, ctx, args) {
    var blocked = false, originalOpen = window.open, result;
    try {
      window.open = function () {
        var w = originalOpen.apply(window, arguments);
        if (!w) { blocked = true; }
        return w;
      };
      result = fn.apply(ctx, args);
    } finally {
      window.open = originalOpen;
    }
    return { count: blocked ? false : true, result: result };
  }

  function attach() {
    var host = document.getElementById(MOUNT_ID);
    if (!host) { return false; }
    var api = window.cliniecoComplianceLog;
    if (!api || typeof api.csv !== 'function') { return false; }
    if (!el || !el.parentNode) { build(host); }
    if (!host.__nudgeClickBound) {
      host.addEventListener('click', onClickCapture, true);
      try { Object.defineProperty(host, '__nudgeClickBound', { value: true, enumerable: false }); }
      catch (e) { host.__nudgeClickBound = true; }
    }
    /* wrap once, delegating to the original implementation - behaviour unchanged */
    if (!api.__nudgeWrapped) {
      var ocsv = api.csv, oprint = api.print;
      api.csv = function () {
        var g = callGuarded(ocsv, this, arguments);
        if (g.count) { bump(); }
        return g.result;
      };
      api.print = function () {
        var g = callGuarded(oprint, this, arguments);
        if (g.count) { bump(); }
        return g.result;
      };
      try { Object.defineProperty(api, '__nudgeWrapped', { value: true, enumerable: false }); }
      catch (e) { api.__nudgeWrapped = true; }
    }
    render();
    return true;
  }

  function start() {
    if (attach()) { return; }
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      if (attach() || tries > 60) { clearInterval(t); }
    }, 250);
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', start); } else { start(); }
  /* expose for acceptance testing only - never used to gate anything */
  window.cliniecoLogNudge = {
    render: render,
    bump: bump,
    readCount: function () { return readInt(COUNT_KEY); },
    readDay: function () { return readStr(DAY_KEY); },
    reissueUrl: REISSUE_URL,
    hasContent: stateHasContent,
    callGuarded: callGuarded
  };
})();
